import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import './AdminDashboard.css'

interface Submission {
  id: string
  created_at: string
  name: string
  email: string
  location: string
  business_name: string
  company_description: string
  service: string
  budget: string
  date_time: string
  status: string
}

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [filteredSubmissions, setFilteredSubmissions] = useState<Submission[]>([])

  useEffect(() => {
    // Check authentication
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate('/admin/login')
        return
      }
      fetchSubmissions()
    }
    checkAuth()
  }, [navigate])

  useEffect(() => {
    if (fromDate || toDate) {
      const filtered = submissions.filter(sub => {
        const submissionDate = new Date(sub.created_at).toISOString().split('T')[0]
        const from = fromDate ? submissionDate >= fromDate : true
        const to = toDate ? submissionDate <= toDate : true
        return from && to
      })
      setFilteredSubmissions(filtered)
    } else {
      setFilteredSubmissions(submissions)
    }
  }, [fromDate, toDate, submissions])

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('Fetching submissions from Supabase...')

      const { data, error } = await supabase
        .from('contact_form_submissions')
        .select('*')
        .order('created_at', { ascending: false })

      console.log('Supabase response:', { data, error })

      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      console.log('Fetched submissions:', data?.length || 0)
      setSubmissions(data || [])
      setFilteredSubmissions(data || [])
    } catch (err: any) {
      console.error('Error fetching submissions:', err)
      setError(err.message || 'Failed to fetch submissions. Check console for details.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/admin/login')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDownloadPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4')

    // Title
    doc.setFontSize(18)
    doc.setTextColor(89, 79, 154) // #594F9A
    doc.text('BOLD! Creative Studio - Form Submissions', 14, 15)

    // Date range info
    if (fromDate || toDate) {
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      const dateRange = `Date Range: ${fromDate || 'Start'} to ${toDate || 'End'}`
      doc.text(dateRange, 14, 22)
    }

    // Table data
    const tableData = filteredSubmissions.map(sub => [
      formatDate(sub.created_at),
      sub.name,
      sub.email,
      sub.location,
      sub.business_name,
      sub.company_description?.substring(0, 50) + (sub.company_description && sub.company_description.length > 50 ? '...' : ''),
      sub.service,
      sub.budget,
      formatDate(sub.date_time),
      sub.status
    ])

    // Use autoTable function (v5.x API)
    autoTable(doc, {
      head: [['Date', 'Name', 'Email', 'Location', 'Business', 'Description', 'Service', 'Budget', 'Date & Time', 'Status']],
      body: tableData,
      startY: fromDate || toDate ? 28 : 25,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [89, 79, 154], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: 14, right: 14 },
      tableWidth: 'wrap'
    })

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(100, 100, 100)
      doc.text(
        `Page ${i} of ${pageCount} | Generated: ${new Date().toLocaleString()}`,
        14,
        (doc as any).internal.pageSize.height - 10
      )
    }

    const fileName = `bold-submissions-${fromDate || 'all'}-${toDate || 'all'}-${Date.now()}.pdf`
    doc.save(fileName)
  }

  const handleDownloadExcel = () => {
    // Prepare data for Excel
    const excelData = filteredSubmissions.map(sub => ({
      'Date': formatDate(sub.created_at),
      'Name': sub.name,
      'Email': sub.email,
      'Location': sub.location,
      'Business Name': sub.business_name,
      'Company Description': sub.company_description || '',
      'Service': sub.service,
      'Budget': sub.budget,
      'Date & Time': formatDate(sub.date_time),
      'Status': sub.status
    }))

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Submissions')

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Date
      { wch: 20 }, // Name
      { wch: 25 }, // Email
      { wch: 20 }, // Location
      { wch: 20 }, // Business Name
      { wch: 40 }, // Company Description
      { wch: 25 }, // Service
      { wch: 15 }, // Budget
      { wch: 20 }, // Date & Time
      { wch: 15 }  // Status
    ]
    ws['!cols'] = colWidths

    // Generate file name
    const fileName = `bold-submissions-${fromDate || 'all'}-${toDate || 'all'}-${Date.now()}.xlsx`

    // Download
    XLSX.writeFile(wb, fileName)
  }

  const handleClearFilters = () => {
    setFromDate('')
    setToDate('')
  }


  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading submissions...</div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>BOLD! Admin Dashboard</h1>
          <p>Form Submissions Management</p>
        </div>
        <button onClick={handleLogout} className="admin-logout-button">
          Logout
        </button>
      </div>

      {error && (
        <div className="admin-error-banner">
          <p>Error: {error}</p>
          <button onClick={fetchSubmissions}>Retry</button>
        </div>
      )}

      <div className="admin-controls">
        <div className="admin-stats">
          <div className="stat-card">
            <span className="stat-label">Total Submissions</span>
            <span className="stat-value">{submissions.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Filtered Results</span>
            <span className="stat-value">{filteredSubmissions.length}</span>
          </div>
        </div>

        <div className="admin-filters">
          <div className="date-range-filters">
            <div className="date-filter-group">
              <label htmlFor="fromDate">From Date:</label>
              <input
                type="date"
                id="fromDate"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="date-filter-group">
              <label htmlFor="toDate">To Date:</label>
              <input
                type="date"
                id="toDate"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate}
              />
            </div>
            {(fromDate || toDate) && (
              <button
                onClick={handleClearFilters}
                className="clear-filter-button"
              >
                Clear Filter
              </button>
            )}
          </div>
          <div className="download-buttons">
            <button
              onClick={handleDownloadPDF}
              className="download-button download-pdf"
              disabled={filteredSubmissions.length === 0}
            >
              📄 Download PDF
            </button>
            <button
              onClick={handleDownloadExcel}
              className="download-button download-excel"
              disabled={filteredSubmissions.length === 0}
            >
              📊 Download Excel
            </button>
          </div>
        </div>
      </div>

      <div className="admin-submissions-table-container">
        {filteredSubmissions.length === 0 ? (
          <div className="admin-empty">
            <p>No submissions found{(fromDate || toDate) ? ` for the selected date range` : ''}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Business Name</th>
                  <th>Company Description</th>
                  <th>Service</th>
                  <th>Budget</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>{formatDate(submission.created_at)}</td>
                    <td>{submission.name}</td>
                    <td>
                      <a href={`mailto:${submission.email}`}>{submission.email}</a>
                    </td>
                    <td>{submission.location}</td>
                    <td>{submission.business_name}</td>
                    <td className="text-cell">{submission.company_description}</td>
                    <td>{submission.service}</td>
                    <td>{submission.budget}</td>
                    <td>{formatDate(submission.date_time)}</td>
                    <td>
                      <span className={`submission-status status-${submission.status}`}>
                        {submission.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard

